<template>
	<div class="messages">
		<div class="row mb-3">
			<div class="col-md-4">
				<label for="date_from">{{$t('from-date')}}</label>
				<input type="date" id="date_from" v-model="search.date_from" @change="getFeedback" class="form-control" />
			</div>
			<div class="col-md-4">
				<label for="date_to">{{$t('to-date')}}</label>
				<input type="date" id="date_to" v-model="search.date_to" @change="getFeedback" class="form-control" />
			</div>
			<div class="col-md-4">
				<div class="form-group">
					<label>{{ $t('students:') }}</label>
					<div class="pseudo-input mb-3">
						<input
							type="search"
							class="input-bare w-full flex-1"
							placeholder="Student"
							v-if="!selectedStudents.length"
							@click="openStudentSelector"
							@keyup.enter="openStudentSelector"
							:aria-label="$t('student')"
						/>
						<span
							class="badge badge-secondary badge-sm mr-2 mb-2"
							v-for="(student, index) in selectedStudents"
							:key="index"
						>
							{{ student.displayName }}
							<a @click="removeStudentFromFilter(student)">
								<b-icon icon="x"></b-icon>
							</a>
						</span>
						<span
							class="badge badge-primary badge-sm mr-2 mb-2"
							v-if="selectedStudents.length"
							@click="openStudentSelector"
							@keyup.enter="openStudentSelector"
							tabindex="0"
							:aria-label="$t('student') + ' ' + $t('add')"
						>
							<b-icon icon="plus"></b-icon> {{ $t("add") }}
						</span>
						<span
							class="badge badge-danger badge-sm mr-2 mb-2"
							v-if="selectedStudents.length"
							@click="clearStudentFilter"
							@keyup.enter="clearStudentFilter"
							tabindex="0"
							:aria-label="$t('student') + ' ' + $t('clear-all')"
						>
							<b-icon icon="x"></b-icon> {{ $t('clear-all') }}
						</span>
					</div>
				</div>
			</div>
		</div>
		
		<!-- Reset Filters Button -->
		<div class="row mb-3">
			<div class="col-md-12 text-right">
				<button class="btn btn-secondary" @click="resetFilters">
					{{ $t('reset-filters') }}
				</button>
			</div>
		</div>
		
		<div class="row">
			<div class="col-6 mb-4">
				<h1>
					{{ username !== null ? $t('treatment-session-feedback-for') + ' ' + username : $t('treatment-session-feedback') }}
				</h1>
			</div>
			<div class="col-6 mb-4">
				<button
					v-if="$UserSession.hasPriv.call(this,'REPORT_TEACHERFEEDBACK')"
					class="btn btn-outline-dark btn-sm ml-2 float-right"
					@click="downloadReport"
				>
					<b-icon icon="download" />
				</button>
			</div>
		</div>

		<!-- InfiniteScroll Component -->
		<InfiniteScroll
			type="TreatmentSessionFeedback"
			:dataUrl="`${this.$settings.APIBaseURL}/treatmentsessions/studentfeedback/listfeedback`"
			dataProperty="feedback"
			class="inspector-list full-width"
			itemClass="inspector-list-item"
			@selectItem="selectFeedback"
			ref="infinitescroll"
			:lookaheadPx="50"
			idProperty="treatmentsession_id"
			:noResultsText="$t('no-results')"
			scrollElementId="main"
		>
			<template v-slot:item="{ item }">
				<div class="inspector-list-item" @click="$emit('selectItem', item)">
					<div class="d-flex justify-content-between">
						<div>
							<strong>{{ getStudentName(item.student_id) }}</strong>
						</div>
						<div>
							{{ showDate(item.treatmentsession_dateoftreatment) }}
						</div>
					</div>
					<div>
						{{ item.response_text }}
					</div>
				</div>
			</template>
		</InfiniteScroll>

		<!-- Selected Feedback Details -->
		<div class="inspector-list-detail active" v-if="selectedFeedback" ref="selectedFeedbackDiv">
			<div class="float-right">
				<router-link
					:to="`/clinical/treatmentsession/${selectedFeedback.treatmentsession_id}`"
					class="btn btn-light btn-xs ml-3 pl-2 pr-2"
					v-if="$UserSession.hasPriv.call(this,'MYTREATMENTSESSIONFEEDBACK_IDENTIFY')"
				>
					{{$t("treatment-session")}}
				</router-link>
				<a class="btn btn-light btn-xs ml-3" @click="closeView">
					<b-icon icon="x" width="1em" height="1em"></b-icon>
				</a>
			</div>
			<h2><strong>{{ showDate(selectedFeedback.treatmentsession_dateoftreatment) }}</strong></h2>
			<hr />
			<template v-for="response in selectedFeedback.responses">
				<div :key="response.response_id" class="pt-0 pb-0">
					<p class="mb-1 mt-0" style="white-space: pre">{{ response.question_text }}</p>
					<div
						class="badge badge-pill badge-primary mb-2"
						:class="{
							'badge-primary': response.response_type == 0,
							'badge-danger': response.response_type == -1,
							'badge-success': response.response_type == 1,
							'btn-emoji': response.response_emoji
						}"
					>
						{{ response.response_emoji ? String.fromCodePoint(response.response_emoji) : response.response_text }}
					</div>
					<hr />
				</div>
			</template>
		</div>
	</div>
</template>

<script>
import InfiniteScroll from "src/components/InfiniteScroll";

export default {
	components: { InfiniteScroll },
	data() {
		return {
			search: {
				q: "",
				teacher: this.$UserSession.getValue("id"),
				date_from: "",
				date_to: "",
				student_ids: []
			},
			students: [], // Expected: array of objects { id, displayName }
			username: null,
			selectedFeedback: null
		};
	},
	provide() {
		return {
			getStudentList: () => this.students
		};
	},
	computed: {
		userId() {
			return this.$route.params["userid"]
				? this.$route.params["userid"]
				: this.$UserSession.getValue("id");
		},
		selectedStudents() {
			return this.students.filter(student => this.search.student_ids.includes(student.id));
		}
	},
	methods: {
		openStudentSelector() {
			this.$users.getStudents.call(this).then(students => {
				this.$store.commit("openModal", {
					type: "selector",
					title: this.$t("select-a-student"),
					multiSelect: true,
					selectCallback: (selections) => {
						this.$store.commit("closeModal");
						let selectionArray = Array.isArray(selections)
							? selections
							: selections && typeof selections === "object"
							? [selections]
							: [];
						if (selectionArray.length) {
							const newIds = selectionArray.map(s => s.id);
							this.search.student_ids = [...new Set([...this.search.student_ids, ...newIds])];
							this.getFeedback();
						}
					},
					selected: this.search.student_ids,
					data: students,
					captionKey: "displayName",
					studentSearchFilters: true
				});
			}).catch(error => {
				console.error("Failed to open student selector:", error);
			});
		},
		removeStudentFromFilter(student) {
			this.search.student_ids = this.search.student_ids.filter(id => id !== student.id);
			this.getFeedback();
		},
		clearStudentFilter() {
			this.search.student_ids = [];
			this.getFeedback();
		},
		getFeedback() {
			const body = {
				q: this.search.q,
				teacher_id: this.userId,
				date_from: this.search.date_from,
				date_to: this.search.date_to,
				student_ids: this.search.student_ids.join(',')
			};
			this.$refs.infinitescroll.getData("replace", body);
		},
		getStudents() {
			this.$users.getStudents.call(this)
				.then((students) => {
					this.students = students;
				})
				.catch((error) => {
					console.error("Failed to fetch students:", error);
				});
		},
		selectFeedback(feedback) {
			this.selectedFeedback = feedback;
			this.$nextTick(() => {
				this.$refs.selectedFeedbackDiv.scroll({ top: 0 });
			});
		},
		closeView() {
			this.selectedFeedback = null;
		},
		showDate(date) {
			if (this.$UserSession.hasPriv.call(this, "MYTREATMENTSESSIONFEEDBACK_IDENTIFY")) {
				return this.$datetime.getLocaleStringFromTimeStamp(date);
			} else {
				return this.$datetime.getMonthYearStringFromTimestamp(date);
			}
		},
		downloadReport() {
			const body = {
				username: this.$UserSession.getValue("username"),
				authToken: this.$UserSession.getValue("authToken"),
				teacher_id: this.userId,
				download: "pdf",
				date_from: this.search.date_from,
				date_to: this.search.date_to,
				student_ids: this.search.student_ids.join(',')
			};
			const url = `${this.$settings.APIBaseURL}/treatmentsessions/studentfeedback/download?` +
				new URLSearchParams(body).toString();
			window.open(url);
		},
		resetFilters() {
			this.search.date_from = "";
			this.search.date_to = "";
			this.search.student_ids = [];
			this.setSearchDefaults();
			this.getFeedback();
		},
		getStudentName(studentId) {
			const student = this.students.find(student => String(student.id) === String(studentId));
			return student ? student.displayName : "Unknown Student";
		},
		setSearchDefaults() {
			this.search = {
				q: "",
				teacher: this.$UserSession.getValue("id"),
				date_from: "",
				date_to: "",
				student_ids: []
			};
		}
	},
	mounted() {
		this.setSearchDefaults();
		this.$nextTick(() => {
			if (this.$refs.infinitescroll) {
				this.getFeedback();
			}
		});
		this.getStudents();
		if (this.$route.params["userid"]) {
			this.$users.getDisplayName.call(this, this.$route.params["userid"]).then((name) => {
				this.username = name;
			});
		}
	}
};
</script>

<style scoped lang="scss">
@import '@/styles/views/feedback.scss';
</style>